<script setup lang="ts">
import {computed, ref, watch} from "vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamHourMovement} from "@/core/interfaces/TeamPlanning";
import {formatSignedHours} from "@/team/planning";
import useHourCounter, {HISTORY_MONTHS} from "@/planning/composables/useHourCounter";
import {useToastStore} from "@/stores/toast.store";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {CalendarClock, Check, CircleMinus, CirclePlus, Pencil, Trash2} from "@lucide/vue";

const DELTA_STEP = 15;
const DELTA_MAX = 24 * 60;

const {open, members, memberId} = defineProps<{
    open: boolean;
    /** Team roster, to label the movements with the member names. */
    members: LegacyAccess[];
    /** Member whose badge was clicked; null opens the whole team's history. */
    memberId: number | null;
}>();

const emits = defineEmits<{ (event: 'update:open', open: boolean): void }>();

const {showError} = useToastStore();
const {
    balances,
    balancesFailed,
    movements,
    movementsLoading,
    movementsFailed,
    fetchMovements,
    updateMovement,
    removeMovement,
    refreshBalances,
} = useHourCounter();

/** The history starts on the clicked member and can be widened to the team. */
const focusedMemberId = ref<number | null>(null);

const editingId = ref<number | null>(null);
const editedDelta = ref<number>(0);
const savingId = ref<number | null>(null);

const loadHistory = () => {
    editingId.value = null;
    refreshBalances();
    fetchMovements(focusedMemberId.value);
};

watch(() => open, opened => {
    if (opened) {
        focusedMemberId.value = memberId;
        loadHistory();
    }
}, {immediate: true});

const showWholeTeam = () => {
    focusedMemberId.value = null;
    fetchMovements(null);
};

const memberNames = computed<Map<number, string>>(() =>
    new Map(members.map(member => [member.id, member.user?.name ?? '']))
);

const nameOf = (movement: TeamHourMovement): string =>
    memberNames.value.get(movement.user_access_point_id) || 'Ancien membre';

const focusedName = computed(() =>
    focusedMemberId.value === null ? null : memberNames.value.get(focusedMemberId.value) || 'Ancien membre'
);

/** "Samedi 26 juillet", the mockup's long date with a leading capital. */
const dateLabel = (movement: TeamHourMovement): string => {
    const label = dayjs(movement.date).locale('fr').format('dddd DD MMMM');

    return label.charAt(0).toUpperCase() + label.slice(1);
};

const startEdit = (movement: TeamHourMovement) => {
    editingId.value = movement.id;
    editedDelta.value = movement.delta_minutes;
};

const nudgeDelta = (direction: 1 | -1) => {
    // The counter refuses a zero movement: stepping across zero skips it.
    let next = editedDelta.value + direction * DELTA_STEP;

    if (next === 0) {
        next += direction * DELTA_STEP;
    }

    editedDelta.value = Math.min(Math.max(next, -DELTA_MAX), DELTA_MAX);
};

const confirmEdit = async (movement: TeamHourMovement) => {
    savingId.value = movement.id;

    try {
        await updateMovement(movement.id, {delta_minutes: editedDelta.value});
        editingId.value = null;
    } catch {
        showError({content: 'Le mouvement n\'a pas pu être modifié'});
    } finally {
        savingId.value = null;
    }
};

const remove = async (movement: TeamHourMovement) => {
    savingId.value = movement.id;

    try {
        await removeMovement(movement.id);
    } catch {
        showError({content: 'Le mouvement n\'a pas pu être supprimé'});
    } finally {
        savingId.value = null;
    }
};
</script>

<template>
  <Dialog :open="open" @update:open="value => emits('update:open', value)">
    <DialogContent class="max-w-xl">
      <DialogHeader>
        <DialogTitle>Compteur d'heures de l'équipe</DialogTitle>
        <p class="text-xs text-gray-400">
          Historique des mouvements d'heures supplémentaires et à rattraper.
        </p>
      </DialogHeader>

      <div v-if="balancesFailed" class="flex items-center gap-2.5 rounded-lg bg-amber-50 px-3.5 py-2.5">
        <span class="flex-1 text-[11.5px] font-medium text-amber-800">
          Les soldes n'ont pas pu être chargés : les totaux ci-dessous ne sont pas à jour.
        </span>
        <button class="text-[11px] font-semibold text-amber-900 hover:underline" @click="refreshBalances">
          Réessayer
        </button>
      </div>

      <div class="flex gap-2.5">
        <div class="flex-1 rounded-lg bg-green-50 px-3 py-2.5">
          <div class="text-[9.5px] font-bold text-green-700">
            HEURES SUPPLÉMENTAIRES
          </div>
          <div class="mt-0.5 text-lg font-bold text-green-700">
            {{ formatSignedHours(balances.total.overtime_minutes) }}
          </div>
        </div>
        <div class="flex-1 rounded-lg bg-amber-50 px-3 py-2.5">
          <div class="text-[9.5px] font-bold text-amber-700">
            À RATTRAPER
          </div>
          <div class="mt-0.5 text-lg font-bold text-amber-700">
            {{ formatSignedHours(-balances.total.recovery_minutes) }}
          </div>
        </div>
        <div class="flex-1 rounded-lg bg-gray-50 px-3 py-2.5">
          <div class="text-[9.5px] font-bold text-gray-400">
            SOLDE NET
          </div>
          <div
            class="mt-0.5 text-lg font-bold"
            :class="balances.total.balance_minutes > 0 ? 'text-green-700'
              : balances.total.balance_minutes < 0 ? 'text-amber-700' : 'text-gray-700'"
          >
            {{ formatSignedHours(balances.total.balance_minutes) }}
          </div>
        </div>
      </div>

      <div class="flex items-baseline gap-2.5">
        <div class="flex-1 text-[11px] font-bold text-gray-500">
          {{ focusedName ? `Historique — ${focusedName}` : 'Historique — toute l\'équipe' }}
          <span class="font-normal text-gray-400">· {{ HISTORY_MONTHS }} derniers mois</span>
        </div>
        <button
          v-if="focusedName"
          class="text-[10.5px] font-semibold text-primary hover:underline"
          @click="showWholeTeam"
        >
          Voir toute l'équipe
        </button>
      </div>

      <div class="max-h-[340px] overflow-y-auto pr-1">
        <div v-if="movementsLoading && !movements.length" class="py-6 text-center text-xs text-gray-400">
          Chargement de l'historique…
        </div>
        <div v-else-if="movementsFailed" class="flex flex-col items-center gap-2 py-6 text-center">
          <p class="text-xs text-gray-500">
            L'historique n'a pas pu être chargé.
          </p>
          <button class="text-[11px] font-semibold text-primary hover:underline" @click="fetchMovements(focusedMemberId)">
            Réessayer
          </button>
        </div>
        <div v-else-if="!movements.length" class="py-6 text-center text-xs text-gray-300">
          Aucun mouvement sur la période.
        </div>
        <div
          v-for="movement in movements"
          :key="movement.id"
          class="flex items-center gap-2.5 border-b border-gray-100 py-2.5"
        >
          <div
            class="flex size-7 flex-none items-center justify-center rounded-lg"
            :class="movement.delta_minutes > 0 ? 'bg-green-50' : 'bg-amber-50'"
          >
            <component
              :is="movement.delta_minutes > 0 ? CirclePlus : CircleMinus"
              class="size-3.5"
              :class="movement.delta_minutes > 0 ? 'text-green-700' : 'text-amber-700'"
            />
          </div>

          <div class="min-w-0 flex-1">
            <div class="truncate text-xs font-semibold text-gray-800">
              {{ nameOf(movement) }} · {{ movement.reason }}
            </div>
            <div class="truncate text-[10.5px] text-gray-400">
              {{ dateLabel(movement) }}<template v-if="movement.note">
                — {{ movement.note }}
              </template>
            </div>
          </div>

          <template v-if="editingId === movement.id">
            <div class="flex flex-none items-center gap-1.5">
              <button
                class="h-7 w-6 rounded-md border border-gray-200 bg-white text-sm text-gray-500 hover:border-primary/40"
                @click="nudgeDelta(-1)"
              >
                −
              </button>
              <span
                class="w-[52px] text-center text-xs font-bold"
                :class="editedDelta > 0 ? 'text-green-700' : 'text-amber-700'"
              >
                {{ formatSignedHours(editedDelta) }}
              </span>
              <button
                class="h-7 w-6 rounded-md border border-gray-200 bg-white text-sm text-gray-500 hover:border-primary/40"
                @click="nudgeDelta(1)"
              >
                +
              </button>
              <button
                title="Valider"
                class="flex size-7 items-center justify-center rounded-md bg-primary text-white disabled:opacity-50"
                :disabled="savingId === movement.id"
                @click="confirmEdit(movement)"
              >
                <Check class="size-3.5" />
              </button>
            </div>
          </template>
          <template v-else>
            <span
              class="flex-none text-xs font-bold"
              :class="movement.delta_minutes > 0 ? 'text-green-700' : 'text-amber-700'"
            >
              {{ formatSignedHours(movement.delta_minutes) }}
            </span>
            <template v-if="movement.team_shift_exception_id === null">
              <button
                title="Modifier"
                class="flex size-6 flex-none items-center justify-center rounded-md border border-gray-200 bg-white text-primary hover:border-primary/40"
                :disabled="savingId === movement.id"
                @click="startEdit(movement)"
              >
                <Pencil class="size-3" />
              </button>
              <button
                title="Supprimer ce mouvement"
                class="flex size-6 flex-none items-center justify-center rounded-md border border-gray-200 bg-white text-red-400 hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                :disabled="savingId === movement.id"
                @click="remove(movement)"
              >
                <Trash2 class="size-3" />
              </button>
            </template>
            <span
              v-else
              title="Issu d'une modification de plage — ajustez la plage horaire dans le planning pour le corriger"
              class="flex size-6 flex-none items-center justify-center text-gray-300"
            >
              <CalendarClock class="size-3.5" />
            </span>
          </template>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
